import React from 'react';
import { Appointment, Payment } from '@/types';
import { CreditCard, Loader2, X } from 'lucide-react';
import { Badge } from '@shared/components/ui/Badge';
import { Modal } from '@shared/components/ui/Modal';
import { toMoneyNumber } from '@/lib/formatters';

interface PaymentDetailModalProps {
  open: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  record: Payment | null;
  isLoading: boolean;
  error: string;
  getPaymentStatusMeta: (
    method: Appointment['paymentMethod'],
    status?: string
  ) => { label: string; variant: 'success' | 'danger' | 'warning' | 'neutral' | 'info' };
  hasInvoicePaymentAnomaly: (app: Appointment) => boolean;
  formatAmount: (amount?: number | string | null, currency?: string | null) => string;
  formatDateTime: (value?: string | Date | null) => string;
}

export const PaymentDetailModal: React.FC<PaymentDetailModalProps> = ({
  open,
  onClose,
  appointment,
  record,
  isLoading,
  error,
  getPaymentStatusMeta,
  hasInvoicePaymentAnomaly,
  formatAmount,
  formatDateTime
}) => (
  <Modal open={open} onClose={onClose} size="lg">
    <div className="border-b bg-slate-50 p-5 flex items-center justify-between">
      <div>
        <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-teal-600" /> Payment Details
        </h3>
        <p className="text-xs text-slate-500">Booking Ref: {appointment?.id || '—'}</p>
      </div>
      <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
        <X className="w-5 h-5" />
      </button>
    </div>
    <div className="p-6 space-y-6">
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading payment details...
        </div>
      ) : !appointment ? (
        <p className="text-sm text-slate-500">No payment selected.</p>
      ) : (
        <div className="space-y-5">
          {error ? (
            <div className="bg-red-50 border border-red-100 text-red-700 text-xs font-semibold rounded-lg p-3">
              {error}
            </div>
          ) : null}
          {hasInvoicePaymentAnomaly(appointment) ? (
            <div className="bg-amber-50 border border-amber-100 text-amber-800 text-xs font-semibold rounded-lg p-3">
              Invoice booking anomaly: a payment record is linked and requires manual
              reconciliation.
            </div>
          ) : null}

          {(() => {
            const statusLabel = getPaymentStatusMeta(
              appointment.paymentMethod,
              record?.status || appointment.paymentStatus
            ).label;
            const statusVariant = getPaymentStatusMeta(
              appointment.paymentMethod,
              record?.status || appointment.paymentStatus
            ).variant;
            const currency = record?.currency || appointment.currency || 'GBP';
            const subtotal = record?.subtotalAmount ?? appointment.subtotalAmount ?? null;
            const vatRate = record?.vatRatePercent ?? appointment.vatRatePercent ?? null;
            const vatAmount = record?.vatAmount ?? appointment.vatAmount ?? null;
            const totalAmount = record?.totalAmount ?? appointment.totalAmount ?? null;
            const provider =
              record?.provider || appointment.paymentProvider || appointment.paymentMethod;

            return (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400">Payment Method</p>
                    <p className="text-sm font-semibold text-slate-800 capitalize">
                      {appointment.paymentMethod}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400">Payment Status</p>
                    <Badge variant={statusVariant}>{statusLabel}</Badge>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400">Provider</p>
                    <p className="text-sm text-slate-700">{provider || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400">
                      Payment Reference
                    </p>
                    <p className="text-sm text-slate-700 break-all">
                      {record?.providerTransactionId ||
                        record?.providerVendorTxCode ||
                        appointment.paymentTransactionId ||
                        appointment.paymentVendorTxCode ||
                        appointment.paymentId ||
                        '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400">Captured At</p>
                    <p className="text-sm text-slate-700">
                      {formatDateTime(record?.capturedAt || appointment.paymentCapturedAt) || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400">Payment ID</p>
                    <p className="text-sm text-slate-700 break-all">
                      {record?.id || appointment.paymentId || '—'}
                    </p>
                  </div>
                </div>

                <div className="border rounded-xl p-4 bg-slate-50">
                  <p className="text-[10px] font-bold uppercase text-slate-400 mb-3">
                    Amount Breakdown
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[10px] uppercase text-slate-400">Subtotal</p>
                      <p className="text-sm font-semibold text-slate-800">
                        {subtotal != null ? formatAmount(subtotal, currency) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-slate-400">VAT Rate</p>
                      <p className="text-sm text-slate-700">
                        {vatRate != null ? `${Number(vatRate).toFixed(2)}%` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-slate-400">VAT Amount</p>
                      <p className="text-sm text-slate-700">
                        {vatAmount != null ? formatAmount(vatAmount, currency) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-slate-400">Total</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {totalAmount != null ? formatAmount(totalAmount, currency) : '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {record?.errorMessage || appointment.paymentErrorMessage ? (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-700">
                    <span className="font-bold">Error:</span>{' '}
                    {record?.errorMessage || appointment.paymentErrorMessage}
                  </div>
                ) : null}

                {record?.metadata ? (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">
                      Payment Metadata
                    </p>
                    <pre className="text-xs bg-slate-900 text-slate-100 rounded-lg p-3 overflow-auto max-h-64">
                      {JSON.stringify(record.metadata, null, 2)}
                    </pre>
                  </div>
                ) : null}
              </>
            );
          })()}
        </div>
      )}
    </div>
  </Modal>
);

// Re-export helper so FinanceTab can use it without importing from formatters directly
export const formatAmount = (amount?: number | string | null, currency?: string | null): string => {
  const normalized = toMoneyNumber(amount || 0);
  const currencyCode = String(currency || '')
    .trim()
    .toUpperCase();
  const prefix = currencyCode && currencyCode !== 'GBP' ? `${currencyCode} ` : '£';
  return `${prefix}${normalized.toFixed(2)}`;
};
